import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { downloadSettingExcel } from "src/utils/blob/blobUtil";

import { WMS2091_R01Filter, useWMS2091_R01Store } from 'src/containers/mes/WMS/WMS2091_R01/Store_WMS2091_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';

import { createWMS2091_R01LotNo, downloadExcel_WMS2091_R01, getWMS2091_R01AllCount, getWMS2091_R01DetailList, getWMS2091_R01List, getWMS2091_R01One, postWMS2091_R01 } from "./Api_WMS2091_R01";
import { defaultWMS2091_R01DetailDto, defaultWMS2091_R01HeaderDto, WMS2091_R01DetailDto, WMS2091_R01Dto, WMS2091_R01HeaderDto } from './Dto_WMS2091_R01';
import { AgGridReact } from 'ag-grid-react';
import { gridRefSelectedRowHandler, InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { validateString } from 'src/utils/string/stringUtil';
import { validateCustomCd, validateEmployeeCd } from 'src/utils/grid/GridValidateContent';
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	WMS2091_R01_DETAIL_ROW_VALIDATE,
	WMS2091_R01_HEADER_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';
import { ItemLabelPrintData } from 'src/apis/itemLabelApi';
import { WMS2091_R01_extractUnprintedItemLabelData } from './labelDataBuilder_WMS2091_R01';
import { useItemQzPrint } from 'src/hooks/useItemQzPrint';
import { getDefaultLabelPrintAuto } from 'src/utils/label/labelPrintAutoProgramUtil';


interface Props {
	gridRef: React.RefObject<AgGridReact<WMS2091_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<WMS2091_R01DetailDto> | null>;

	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;

	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
	
}

const FIXED_TRANSPORT_DT = '2026-01-01';
const FIXED_INOUT_CLASS = '100';

export const Content_WMS2091_R01 = (props: Props) => {

	const {
		gridRef, detailGridRef,
		tabList, setMessage,
		containerRef,
		isResizing,
	} = props;

	
	const {
		WMS2091_R01Filter, setWMS2091_R01Filter,
		WMS2091_R01PastFilter, setWMS2091_R01PastFilter,
		WMS2091_R01List, setWMS2091_R01List,
		editWMS2091_R01, setEditWMS2091_R01,
		selectedWMS2091_R01, setSelectedWMS2091_R01, 
		deletedWMS2091_R01DetailList, setDeletedWMS2091_R01DetailList, addDeletedWMS2091_R01DetailList, removeDeletedWMS2091_R01DetailList, 
		selectedWMS2091_R01DetailList, setSelectedWMS2091_R01DetailList, addSelectedWMS2091_R01DetailList, removeSelectedWMS2091_R01DetailList,
		WMS2091_R01DetailList, setWMS2091_R01DetailList, insertWMS2091_R01DetailList,
		originalWMS2091_R01DetailList, setOriginalWMS2091_R01DetailList,
		allCount, setAllCount,
		detailFetchTrigger, setDetailFetchTrigger,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = useWMS2091_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { inoutClassList, warehouseCdList, inoutMethodList, wmsCdList, compCdList, customCdList, employeeCdList, wmsOutTypeCdList, itemStatusList, programList } = useCommonCodeStore();

	const defaultLabelPrintAuto = useMemo(
		() => getDefaultLabelPrintAuto(programList),
		[programList]
	);
	const { printBulkItemLabel } = useItemQzPrint();
	useFetchActionbarAuth("WMS2091_R01");

	const [clickable, setClickable] = useState(true);

	const prevTransportNoRef = useRef<string | undefined>(undefined);
	const currentDetailListRef = useRef<WMS2091_R01DetailDto[]>([]);

	const handleExcelClick = useCallback(async () => {
		if (!selectedWMS2091_R01) {
			showWarning("선택된 입고정보내역이 없습니다.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const response = await downloadExcel_WMS2091_R01('', selectedWMS2091_R01.transportNo, editWMS2091_R01!.warehouseCd!, WMS2091_R01List.length ?? 0);
			downloadSettingExcel({ responseData: response.data.data, fileName: '입고정보 등록-excel.xlsx' });
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedWMS2091_R01])

	const getParams = (filter: WMS2091_R01Filter) => {
		const params: Partial<WMS2091_R01Filter> = {
			transportNo: filter.transportNo || undefined,
			customCd: filter.customCd || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getWMS2091_R01AllCount(getParams(WMS2091_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [WMS2091_R01Filter, setAllCount]);

	const fetchData = useCallback(async (skipSelection?: boolean) => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);

		try {
			const responseData = (await getWMS2091_R01List(
				'',
				getParams(WMS2091_R01Filter),
				WMS2091_R01List.length ?? 0
			));

			let newList = [...responseData.data.data ] as WMS2091_R01HeaderDto[];
			setWMS2091_R01List(newList);

			const processAllCount = await renewalAllCount();
			if (newList && newList.length > 0) {
				if (skipSelection !== true) {
					gridRefSelectedRowHandler(
						gridRef,
						setSelectedWMS2091_R01,
						() => true,
					);
				}
				setWMS2091_R01PastFilter(WMS2091_R01Filter);
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}else {
				showWarning('데이터가 없습니다.');
				handleCreateClick();
			}
			return responseData;
		} catch (error: any) {
			showError(error?.response?.data?.message || '조회 중 오류가 발생했습니다.');
			return null;
		} finally {
			setLoadingSpinner(false);
		}
	}, [WMS2091_R01Filter, WMS2091_R01List, setWMS2091_R01List, setWMS2091_R01PastFilter, setSelectedWMS2091_R01, selectedWMS2091_R01, setScrollTop, setDetailScrollTop])

	const fetchOne = useCallback(async () => {
		if (!selectedWMS2091_R01) return;
		
		const response = await getWMS2091_R01One(selectedWMS2091_R01.transportNo);

		return response.data.data;
	}, [selectedWMS2091_R01]);

	const defaultWMS2091_R01HeaderRow = useCallback(() => {
		const logicCenter = wmsCdList[0]?.minorCd ?? '';
		const warehouseCdListToUse = warehouseCdList.filter(value =>
			value.logicCenter === logicCenter && value.materialYn === 'Y'
		);

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(WMS2091_R01_HEADER_ROW_VALIDATE, {
				compCdList,
				inoutMethodList,
				wmsCdList,
				warehouseCdList: warehouseCdListToUse,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultWMS2091_R01HeaderDto };
		}

		return {
			...defaultWMS2091_R01HeaderDto,
			transportDt: FIXED_TRANSPORT_DT,
			inoutClass: FIXED_INOUT_CLASS, // 기초재고입고 값 고정
			inoutType: 'IN',
			inoutMethod: inoutMethodList[0].minorCd,
			compCd: compCdList[0].compCd,
			logicCenter,
			warehouseCd: warehouseCdListToUse[0].warehouseCd,
			inserted: true,
		};
	}, [
		defaultWMS2091_R01HeaderDto, wmsCdList, warehouseCdList, inoutMethodList, compCdList,
		validateCommonCodeLists,
	]);

	const defaultWMS2091_R01DetailRow = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(WMS2091_R01_DETAIL_ROW_VALIDATE, {
				itemStatusList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultWMS2091_R01DetailDto };
		}

		return {
			...defaultWMS2091_R01DetailDto,
			itemStatus: itemStatusList[0].minorCd,
			inserted: true,
		};
	}, [defaultWMS2091_R01DetailDto, itemStatusList, validateCommonCodeLists]);
	
	const initContainerData = useCallback(() => {
		initData();
		setWMS2091_R01Filter({ labelPrintAuto: defaultLabelPrintAuto });
		removeDeletedWMS2091_R01DetailList();
		removeSelectedWMS2091_R01DetailList();
		setEditWMS2091_R01({ ...defaultWMS2091_R01HeaderRow(), })
	}, [initData, defaultLabelPrintAuto, setWMS2091_R01Filter, removeDeletedWMS2091_R01DetailList, removeSelectedWMS2091_R01DetailList, setEditWMS2091_R01, defaultWMS2091_R01HeaderRow])

	const handleDeleteClick = useCallback(() => {

		if (deletedWMS2091_R01DetailList.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedWMS2091_R01DetailList(deletedWMS2091_R01DetailList);
			removeSelectedWMS2091_R01DetailList();
			setWMS2091_R01DetailList([...WMS2091_R01DetailList]);
		} else if (selectedWMS2091_R01DetailList.length > 0) {
			addDeletedWMS2091_R01DetailList(selectedWMS2091_R01DetailList);
			removeSelectedWMS2091_R01DetailList(selectedWMS2091_R01DetailList);
		}
	}, [selectedWMS2091_R01DetailList, 
		deletedWMS2091_R01DetailList,
		addDeletedWMS2091_R01DetailList,
		removeDeletedWMS2091_R01DetailList,
		removeSelectedWMS2091_R01DetailList,
		setWMS2091_R01DetailList,
		WMS2091_R01DetailList]);
	
	const handleCreateClick = async () => {
		setSelectedWMS2091_R01(undefined);
		setDetailFetchTrigger(false);
		setEditWMS2091_R01({ ...defaultWMS2091_R01HeaderRow(), })
		setWMS2091_R01DetailList([]);
		await insertWMS2091_R01DetailList([{ ...defaultWMS2091_R01DetailRow() }]);
		setDeletedWMS2091_R01DetailList([]);
		setSelectedWMS2091_R01DetailList([]);
		setOriginalWMS2091_R01DetailList([]);
	}

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
		const isGridInsert = rowIndex !== undefined;

		InsertRowHandler({
			event: e,
			dataList: WMS2091_R01DetailList,
			rowData: isGridInsert
				? WMS2091_R01DetailList[rowIndex]
				: { ...defaultWMS2091_R01DetailRow(), valueIndex: -1 },
			insertRow: insertWMS2091_R01DetailList,
			defaultRowData: defaultWMS2091_R01DetailRow(),
			...(isGridInsert && { setClickable }),
		});
	}, [WMS2091_R01DetailList, insertWMS2091_R01DetailList, defaultWMS2091_R01DetailRow, setClickable]);

	const validateInsertData = (header: WMS2091_R01HeaderDto, detailList: WMS2091_R01DetailDto[]) => {
		
		// if(!validateString(header.transportNo)) {
		// 	showWarning('입고지시번호를 입력해주세요.'); return false;
		// }
		if (!validateString(header.compCd)) {
			showWarning('사업장을 선택해주세요.'); return false;
		}
		if (!validateString(header.customCd)) {
			showWarning('거래처를 입력해주세요.'); return false;
		}
		if (!validateCustomCd(header.customCd, customCdList)) {
			showWarning('거래처가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}
		if (!validateString(header.logicCenter)) {
			showWarning('물류센터를 선택해주세요.'); return false;
		}
		if (!validateString(header.warehouseCd)) {
			showWarning('창고번호를 선택해주세요.'); return false;
		}
		// if (!validateString(header.empNo)) {
		// 	showWarning('입고담당자를 선택해주세요.'); return false;
		// }
		// if (!validateEmployeeCd(header.empNo, employeeCdList)) {
		// 	showWarning('입고담당자가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		// }
		if(!validateString(header.inoutMethod)) {
			showWarning('입고구분을 선택해주세요.'); return false;
		}

		if(detailList.length === 0) {
			showWarning('상세내역을 입력해주세요.'); return false;
		}

		for(const item of detailList) {
			if(!validateString(item.itemCd)) {
				showWarning('품목코드를 입력해주세요.'); return false;
			}
			if(!validateString(item.itemStatus)) {
				showWarning('품목의 상태를 선택해주세요.'); return false;
			}
			if(item.qty <= 0) {
				showWarning('수량을 입력해주세요.'); return false;
			}

			const activeDetailList = detailList.filter(detail => !deletedWMS2091_R01DetailList.includes(detail.valueIndex.toString()));
			const otherDetailList = activeDetailList.filter((_, index) => index !== detailList.findIndex(detail => detail.itemCd === _.itemCd));
			if (otherDetailList.some(otherDetail => otherDetail.itemCd === item.itemCd)) {
				showWarning("중복된 품목코드가 있습니다.\n품목코드: " + item.itemCd);
				return false;
			}

			if(item.stockQty < item.updateTransportQty) {
				showWarning(`입고량이 재고량보다 클 수 없습니다. - 품목코드: ${item.itemCd}`); return false;
			}

		}
		

		return true;
	}

	const setInfoDetailList = useCallback((header: WMS2091_R01HeaderDto, detailList: WMS2091_R01DetailDto[]) => {
		
		return detailList.map((value, idx) => ({
			...value,
			compCd: header.compCd,
			customCd: header.customCd,
		}))
	}, [setWMS2091_R01DetailList])

	const assignLotNoToMaps = useCallback(async (currentDetailList: WMS2091_R01DetailDto[]) => {
		const newDetailList = currentDetailList.map(detail => ({ ...detail }));
		const promises: Promise<void>[] = [];

		for (const detail of newDetailList) {
			if (!validateString(detail.lotNo)) {
				const promise = createWMS2091_R01LotNo().then((res) => {
					if (res.data && res.data.successFlag) {
						detail.lotNo = res.data.data;
					}
				});
				promises.push(promise);
			}
		}

		if (promises.length > 0) {
			await Promise.all(promises);
			setWMS2091_R01DetailList(newDetailList);
		}

		return newDetailList;
	}, [setWMS2091_R01DetailList]);
	
	const handleSaveClick = useCallback(async () => {
		setLoadingSpinner(true);

		const fixedHeader = {
			...editWMS2091_R01!,
			transportDt: FIXED_TRANSPORT_DT,
			inoutType: 'IN',
			inoutClass: FIXED_INOUT_CLASS,
			inoutMethod: editWMS2091_R01?.inoutMethod || (inoutMethodList[0] ? inoutMethodList[0].minorCd : ''),
		};

		let valid = true;
		if(!validateInsertData(fixedHeader, WMS2091_R01DetailList)) valid = false;
		if(!valid) { setLoadingSpinner(false); return; }

		const isExistingTransport = validateString(fixedHeader?.transportNo || '')
			&& WMS2091_R01List.some(row => row.transportNo === fixedHeader?.transportNo);
		if (isExistingTransport) {
			showWarning('저장된 입고정보는 수정할 수 없습니다. 등록버튼을 눌러 신규 등록해주세요.');
			setMessage('저장된 입고정보는 수정할 수 없습니다.');
			setTimeout(() => setMessage(''), 3000);
			setLoadingSpinner(false);
			return;
		}

		let processName = '';
		let newHeaderList: WMS2091_R01HeaderDto[] = [];
		let newDetailList: WMS2091_R01DetailDto[] = [];
		let responseData: WMS2091_R01Dto = {
			wmsTransportHeaderDto: fixedHeader,
			wmsTransportDetailDtoList: WMS2091_R01DetailList
		};

		newDetailList = await assignLotNoToMaps(WMS2091_R01DetailList);

		let unprintedItemLabelData: ItemLabelPrintData[] = [];
		if (WMS2091_R01Filter.labelPrintAuto === 'Y') {
			const store = useWMS2091_R01Store.getState();
			unprintedItemLabelData = WMS2091_R01_extractUnprintedItemLabelData(
				store,
				deletedWMS2091_R01DetailList
			);
		}

		try {
			const payload: WMS2091_R01Dto = {
				wmsTransportHeaderDto: fixedHeader,
				wmsTransportDetailDtoList: setInfoDetailList(fixedHeader, newDetailList)
			};

			processName = 'post';
			const postResponse = await postWMS2091_R01(payload);
			responseData = postResponse.data.data;

			if (postResponse.data.successFlag) {
				showSuccess('입고내역이 등록되었습니다.');
				setMessage('입고내역이 등록되었습니다.');
			} else {
				showWarning('입고정보 등록 후 서버로부터 올바른 데이터를 받지 못했습니다.');
				setLoadingSpinner(false);
				return;
			}
		} catch (error: any) {
			showError(error.response?.data?.message || '입고내역 저장 중 오류가 발생했습니다.');
			setLoadingSpinner(false);
			return;
		}

		if (processName === 'post') {
			newHeaderList = [
				...WMS2091_R01List,
				responseData.wmsTransportHeaderDto,
			] as WMS2091_R01HeaderDto[];
			setWMS2091_R01List(newHeaderList);
			setSelectedWMS2091_R01(responseData.wmsTransportHeaderDto);
			setWMS2091_R01DetailList(responseData.wmsTransportDetailDtoList);
			setOriginalWMS2091_R01DetailList(responseData.wmsTransportDetailDtoList);
		}

		setDeletedWMS2091_R01DetailList([]);
		setTimeout(() => {
			setMessage('');
		}, 5000)
		setLoadingSpinner(false);

		if (
			WMS2091_R01Filter.labelPrintAuto === 'Y'
			&& unprintedItemLabelData.length > 0 && processName !== 'delete'
		) {
			try {
				const ok = await printBulkItemLabel(unprintedItemLabelData);
				if (ok) {
					showSuccess(`저장 완료! ${unprintedItemLabelData.length}개 라벨 출력`);
				} else {
					showWarning('저장은 완료되었으나 라벨 출력에 실패했습니다. 프린터 설정을 확인해주세요.');
				}
			} catch (error: any) {
				showWarning(`저장은 완료되었으나 라벨 출력에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
			}
		}
	}, [
		WMS2091_R01Filter,
		editWMS2091_R01,
		WMS2091_R01DetailList,
		deletedWMS2091_R01DetailList,
		WMS2091_R01List, setWMS2091_R01List,
		assignLotNoToMaps,
		printBulkItemLabel,
	])

	//헤더의 정보를 받아서 기본값이 null이 아닌 빈 문자열 값을 찾아서 시스템코드의 기본값으로 표시되게 하기
	const handleDetailInfoCode = useCallback((headerData: WMS2091_R01HeaderDto): Partial<WMS2091_R01HeaderDto> => {
		const defaults: Partial<WMS2091_R01HeaderDto> = {};
	
		if (!validateString(headerData.inoutMethod) && inoutMethodList.length > 0) defaults.inoutMethod = inoutMethodList[0].minorCd;

		if (!validateString(headerData.logicCenter) && wmsCdList.length > 0) { defaults.logicCenter = wmsCdList[0].minorCd; }
		defaults.logicCenter = headerData.logicCenter ? headerData.logicCenter : defaults.logicCenter;

		if (!validateString(headerData.compCd) && compCdList.length > 0) defaults.compCd = compCdList[0].compCd;

		let warehouseCdListToUse = warehouseCdList.filter(value => (value.logicCenter === defaults.logicCenter
			&& value.materialYn === 'Y'
		));
		if (!validateString(headerData.warehouseCd) && warehouseCdListToUse.length > 0) {
			defaults.warehouseCd = warehouseCdListToUse[0].warehouseCd;
		}
		defaults.warehouseCd = headerData.warehouseCd ? headerData.warehouseCd : defaults.warehouseCd;

		defaults.inoutClass = FIXED_INOUT_CLASS;
		defaults.transportDt = FIXED_TRANSPORT_DT;
	
		return defaults;
	}, [inoutMethodList, wmsCdList, compCdList, warehouseCdList, wmsOutTypeCdList]);
	
	const handleFetchDetailList = useCallback(async (transportNo: string, warehouseCd: string, isNewSelection: boolean) => {
		if (!warehouseCd) {
			showError('수주 정보의 물류정보의 조건에 맞는 창고 코드를 찾을 수 없어 입고 목록 내역을 조회할 수 없습니다.', 5000);
			setWMS2091_R01DetailList([]);
			return [];
		}

		// 이전과 같은 전표를 다시 선택한 경우라도, 현재 상세가 비어 있으면 재조회해야 함
		// (단건 조회/재클릭 시 상세가 비는 현상 방지)
		// const currentDetails = useWMS2091_R01Store.getState().WMS2091_R01DetailList;

		// // if (!isNewSelection && currentDetails.length > 0) return currentDetails;

		setLoadingSpinner(true);

		let updatedDetails: WMS2091_R01DetailDto[] = [];
		try {
			const detailResponse = await getWMS2091_R01DetailList(transportNo, warehouseCd);
			const detailData = detailResponse.data.data;

			if (detailData && detailData.length > 0) {
				const currentDetailList = useWMS2091_R01Store.getState().WMS2091_R01DetailList;
				
				// itemCd와 itemStatus를 조합한 복합 키로 Map을 생성하여 정확도를 높입니다.
				const existingItemsMap = new Map<string, WMS2091_R01DetailDto>();
				currentDetailList.forEach(item => {
					if (item.itemCd && item.itemStatus) {
						const key = `${item.itemCd}-${item.itemStatus}`;
						existingItemsMap.set(key, item);
					}
				});

				updatedDetails = detailData.map((value) => {
					return {
						...value,
						valueIndex: value.valueIndex,
						orderIndex: value.orderIndex,
						orderIndexSeq: value.orderIndexSeq,
						stockQty: value.stockQty ?? 0,
					}
				});
				setWMS2091_R01DetailList(updatedDetails);
				setOriginalWMS2091_R01DetailList(detailData);
				setMessage(`${selectedWMS2091_R01?.compNm}의 입고지시내역 ${detailData.length}건을 조회하였습니다.`);
				setTimeout(() => setMessage(""), 5000);

			} else {
				showError('상세내역이 없습니다.');
				setWMS2091_R01DetailList([]);
				return [];
			}
		} catch (error) {
			console.error("상세 내역 조회 실패:", error);
			showError('상세 내역을 조회하는 중 오류가 발생했습니다.');
			setWMS2091_R01DetailList([]);
			setDetailFetchTrigger(false);
			return [];
		} finally {
			setLoadingSpinner(false);
		}

		return updatedDetails;
	}, [setMessage, setWMS2091_R01DetailList, setOriginalWMS2091_R01DetailList, selectedWMS2091_R01?.compNm]);


	useEffect(() => {
		currentDetailListRef.current = WMS2091_R01DetailList.map(value => ({
			...value,
			updateTransportQty: value.updateTransportQty,
		}));
	}, [WMS2091_R01DetailList]);
	
	
	useEffect(() => {
		if (!detailFetchTrigger) return;

		const fetchAllDetailsForSelectedRow = async () => {
			if (!selectedWMS2091_R01 || !selectedWMS2091_R01.transportNo) {
				if (!selectedWMS2091_R01) {
					prevTransportNoRef.current = undefined;
					setWMS2091_R01DetailList([]);
					setOriginalWMS2091_R01DetailList([]);
				}
				return;
			}

			// 이전에 선택한 입고정보와 현재 선택한 입고정보가 다른지 확인
			// 다르면 상세 내역 조회 시 입고량 초기화
			const previousTransportNo = prevTransportNoRef.current;
			const currentTransportNo = selectedWMS2091_R01.transportNo;

			const isNewSelection = previousTransportNo !== currentTransportNo;

			// 1. 입고 정보 먼저 가져오기
			const headerData = await fetchOne();

			// 2. 현재 선택된 행과 API 데이터를 합쳐 기본값을 적용할 베이스 데이터를 만듭니다.
			const baseDataForDefaults = { ...defaultWMS2091_R01HeaderRow(), ...headerData };
			const defaultValues = handleDetailInfoCode(baseDataForDefaults);

			//최종적으로 사용할 warehouseCd, logicCenter 결정
			const logicCenterToUse = baseDataForDefaults.logicCenter || defaultValues.logicCenter || '';
			const warehouseCdToUse = baseDataForDefaults.warehouseCd || defaultValues.warehouseCd || '';

			// 3. 입고 헤더 정보 업데이트
			setEditWMS2091_R01({
				...defaultWMS2091_R01HeaderRow(),
				...headerData,					// 선택된 행의 정보가 베이스
				...defaultValues,				// selectedWMS2091_R01의 기본값으로 비어있는 필드 채우기
				logicCenter: logicCenterToUse,
				warehouseCd: warehouseCdToUse,
				transportDt: FIXED_TRANSPORT_DT,
				inoutClass: FIXED_INOUT_CLASS,
			});

			// 4. 분리된 함수를 호출하여 상세 내역을 등록.
			await handleFetchDetailList(selectedWMS2091_R01.transportNo, warehouseCdToUse, isNewSelection);
			setDetailFetchTrigger(false);
			// 이전
			prevTransportNoRef.current = selectedWMS2091_R01.transportNo;
		};

		fetchAllDetailsForSelectedRow();
	}, [selectedWMS2091_R01, handleDetailInfoCode, setEditWMS2091_R01, handleFetchDetailList, detailFetchTrigger, setDetailFetchTrigger]);

	useEffect(() => {
		setWMS2091_R01Filter({ labelPrintAuto: defaultLabelPrintAuto });
	}, [defaultLabelPrintAuto, setWMS2091_R01Filter]);
	
	return {
		clickable, setClickable,
		handleExcelClick,
		fetchData,
		defaultWMS2091_R01HeaderRow,
		defaultWMS2091_R01DetailRow,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleInsertClick,
		handleSaveClick,
		handleDetailInfoCode,
		handleFetchDetailList,
	};
}


